import { Injectable } from '@angular/core';
import { Action, NgxsOnInit, State, StateContext, Store } from '@ngxs/store';
import { catchError, EMPTY, lastValueFrom, mergeMap, Observable, Subject, Subscription, firstValueFrom, take, filter } from 'rxjs';
import {
	TrovataAppState,
	TROVATA_APP_STATES_TO_CLEAR,
	TROVATA_APP_STATES_TO_INIT,
	TROVATA_FEATURE_STATES_TO_CLEAR,
	TROVATA_FEATURE_STATES_TO_INIT,
	TROVATA_PAYMENTS_STATES_TO_CLEAR,
	TROVATA_PAYMENTS_STATES_TO_INIT,
	TrovataStateAction,
} from 'src/app/core/models/state.model';
import {
	ClearCoreState,
	ClearPaymentsStates,
	ClearTrovataAppStates,
	ClearTrovataFeatureState,
	InitCoreState,
	InitPaymentsStates,
	InitTrovataAppStates,
	InitTrovataFeatureState,
	ResetCoreState,
	ResetPaymentsStates,
	ResetTrovataAppStates,
	ResetTrovataFeatureState,
	SetAppReady,
	SetUATEnvironment,
} from '../../actions/core.actions';
import { environment } from 'src/environments/environment';
import { InitCustomerFeatureState } from 'src/app/features/settings/store/actions/customer-feature.actions';

export class CoreStateModel {
	appReady: boolean;
	uatEnvironment: boolean;
}

@State<CoreStateModel>({
	name: 'core',
	defaults: {
		appReady: null,
		uatEnvironment: null,
	},
})
@Injectable()
export class CoreState implements NgxsOnInit {
	private authenticated$: Observable<boolean>;
	private authenticatedSub: Subscription;

	constructor(private store: Store) {
		this.authenticated$ = this.store.select((state: TrovataAppState) => state.authorization.authenticated);
	}

	ngxsOnInit(context: StateContext<CoreStateModel>): void {
		context.dispatch(new InitCoreState());
	}

	@Action(InitCoreState)
	async initCoreState(context: StateContext<CoreStateModel>): Promise<void> {
		await firstValueFrom(this.authenticated$.pipe(filter(Boolean), take(1)));
		context.dispatch(new SetAppReady(true));
		await lastValueFrom(context.dispatch([new InitCustomerFeatureState()]));
		context.dispatch(new InitTrovataAppStates());
	}

	@Action(SetUATEnvironment)
	setUAT(context: StateContext<CoreStateModel>, action: SetUATEnvironment): void {
		if (action.uatEnvironment) {
			environment.paymentsUat = true;
		} else {
			environment.paymentsUat = false;
		}
		context.patchState({ uatEnvironment: action.uatEnvironment });
	}

	@Action(SetAppReady)
	setAppReady(context: StateContext<CoreStateModel>, action: SetAppReady): void {
		context.patchState({ appReady: action.appReady });
	}

	@Action(InitTrovataAppStates)
	async initTrovataAppStates(context: StateContext<CoreStateModel>): Promise<void> {
		await this.asyncForEach(TROVATA_APP_STATES_TO_INIT);
	}

	@Action(InitPaymentsStates)
	initPaymentsStates(context: StateContext<CoreStateModel>): Promise<void> | void {
		return this.asyncForEach(TROVATA_PAYMENTS_STATES_TO_INIT);
	}

	@Action(ClearCoreState)
	clearCoreState(context: StateContext<CoreStateModel>): void {
		this.authenticatedSub.unsubscribe();
		context.patchState({ appReady: null, uatEnvironment: null });
	}

	@Action(ResetCoreState)
	resetCoreState(context: StateContext<CoreStateModel>): void {
		context.dispatch(new ClearCoreState());
		context.dispatch(new InitCoreState());
	}

	@Action(ClearTrovataAppStates)
	clearTrovataAppStates(context: StateContext<CoreStateModel>): Promise<void> {
		return this.asyncForEach(TROVATA_APP_STATES_TO_CLEAR);
	}

	@Action(InitTrovataFeatureState)
	InitTrovataFeatureState(context: StateContext<CoreStateModel>): Promise<void> {
		return this.asyncForEach(TROVATA_FEATURE_STATES_TO_INIT);
	}

	@Action(ClearTrovataFeatureState)
	clearTrovataFeatureState(context: StateContext<CoreStateModel>): Promise<void> {
		return this.asyncForEach(TROVATA_FEATURE_STATES_TO_CLEAR);
	}

	@Action(ResetTrovataFeatureState)
	async resetTrovataFeatureState(context: StateContext<CoreStateModel>): Promise<void> {
		await lastValueFrom(context.dispatch(new ClearTrovataFeatureState()));
		await lastValueFrom(context.dispatch(new InitTrovataFeatureState()));
	}

	@Action(ClearPaymentsStates)
	clearPaymentsStates(context: StateContext<CoreStateModel>): Promise<void> {
		return this.asyncForEach(TROVATA_PAYMENTS_STATES_TO_CLEAR);
	}

	@Action(ResetTrovataAppStates)
	async resetTrovataAppStates(context: StateContext<CoreStateModel>): Promise<void> {
		await lastValueFrom(context.dispatch(new ClearTrovataAppStates()));
		await lastValueFrom(context.dispatch(new InitTrovataAppStates()));
	}

	@Action(ResetPaymentsStates)
	async resetPaymentsStates(context: StateContext<CoreStateModel>): Promise<void> {
		await lastValueFrom(context.dispatch(new ClearPaymentsStates()));
		await lastValueFrom(context.dispatch(new InitPaymentsStates()));
	}

	private asyncForEach(statesToInit: TrovataStateAction[], maxConcurrentStateInits: number = Infinity): Promise<void> {
		return new Promise((resolve, reject) => {
			const initState$: Subject<TrovataStateAction> = new Subject();
			initState$
				.pipe(mergeMap((stateToInit: TrovataStateAction) => this.store.dispatch(stateToInit).pipe(catchError(error => EMPTY)), maxConcurrentStateInits))
				.subscribe({
					next: () => {
						// nothing
					},
					error: () => {
						// nothing
					},
					complete: () => {
						resolve();
					},
				});
			statesToInit.forEach((stateToInit: TrovataStateAction) => {
				initState$.next(stateToInit);
			});
			initState$.complete();
		});
	}
}
